import { useEffect, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Circle, GeoJSON, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";

const AREA_RADIUS_M = 1800;

const LONDON = [51.5072, -0.1276];
const KEY_SVG = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></svg>';

function pinIcon(color, pulse, highlighted, label, img) {
  const cls = `ww-pin${pulse ? " ww-pin-pulse" : ""}${highlighted ? " ww-pin-hl" : ""}${label ? " ww-pin-num" : ""}${img ? " ww-pin-photo" : ""}`;
  const size = img ? (pulse ? 38 : 32) : pulse ? 34 : label ? 28 : 24;
  const inner = img ? `<img src="${img}" alt="" />` : pulse ? KEY_SVG : label || "";
  return L.divIcon({
    className: "ww-pin-wrap",
    html: `<div class="${cls}" style="--pin:${color};width:${size}px;height:${size}px">${inner}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

const FitBounds = ({ points, fitKey }) => {
  const map = useMap();
  const sig = fitKey ?? points.map((p) => p.key).join("|");
  useEffect(() => {
    map.stop();
    if (!points.length) {
      map.setView(LONDON, 10);
      return;
    }
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [40, 40], maxZoom: 15, animate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, sig]);
  return null;
};

const Pin = ({ p }) => {
  const icon = useMemo(() => pinIcon(p.color, p.pulse, p.highlighted, p.label, p.img), [p.color, p.pulse, p.highlighted, p.label, p.img]);
  if (p.area) {
    const color = p.color || "#1d4ed8";
    const style = { color, fillColor: color, fillOpacity: 0.2, weight: 2 };
    if (p.boundary) {
      return (
        <GeoJSON key={p.key} data={{ type: "Feature", geometry: p.boundary }} style={style}>
          <Popup>{p.popup}</Popup>
        </GeoJSON>
      );
    }
    return (
      <Circle center={[p.lat, p.lng]} radius={AREA_RADIUS_M} pathOptions={style}>
        <Popup>{p.popup}</Popup>
      </Circle>
    );
  }
  return (
    <Marker position={[p.lat, p.lng]} icon={icon} zIndexOffset={p.highlighted ? 1000 : p.pulse ? 500 : 0}>
      <Popup>{p.popup}</Popup>
    </Marker>
  );
};

export const TeamMap = ({ points, fitKey, emptyText, className = "h-[420px]", testId = "team-map" }) => (
  <div className={`relative isolate overflow-hidden rounded-xl border border-slate-200 bg-slate-100 ${className}`} data-testid={testId}>
    <MapContainer center={LONDON} zoom={10} scrollWheelZoom className="h-full w-full">
      <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <FitBounds points={points} fitKey={fitKey} />
      {points.map((p) => <Pin key={p.key} p={p} />)}
    </MapContainer>
    {emptyText && (
      <div className="pointer-events-none absolute inset-x-0 top-4 z-[500] flex justify-center">
        <span className="rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-slate-700 shadow" data-testid={`${testId}-empty`}>{emptyText}</span>
      </div>
    )}
  </div>
);
